import { openProfileDocument } from "../../../../backend/transport";
import React, { useEffect, useState } from 'react';
import { useLoader } from '../../../../context/LoaderContext';
import { useDebounce } from '../../../../hooks';
import { popup } from '../../../../utils';
import { MfgTable } from './MfgTable';
import { Button, Input, PopUpBox } from '../../../shared';
import { DropDown } from '../../../common';
import { getPrint, generateThreePackBlp, getPackageList } from '../../../../service/manufacturing';
import {
  BLP_COLUMNS,
  BUTTON,
  HEADING,
  POPUP_TYPE,
  POPUP_MSG,
  TEXT,
  DROPDOWN,
  EDIT_BOX_STATE,
} from '../../../../constants';

/**
 * A batch's three_pack metadata is only usable if it's flagged available, carries
 * positive pack/box sizes, and the batch has at least one full box packaged —
 * bad metadata or a sub-box quantity should never open a dead-end dialog.
 */
const isThreePackEligible = (threePack, packQty) =>
  Boolean(threePack?.available) &&
  threePack.units_per_pack > 0 &&
  threePack.packs_per_box > 0 &&
  packQty >= threePack.units_per_pack * threePack.packs_per_box;

/**
 * BoxInfo - Box information form component
 * @param {Array} selectedPackage - Array of selected package IDs
 * @param {Function} setOpenPopUp - Function to control popup visibility
 * @param {Function} setBoxInfo - Function to set box information
 */
const BoxInfo = ({ selectedPackage, setOpenPopUp, setBoxInfo }) => {
  const [option, setOption] = useState(DROPDOWN.BOX_OPTION[0]);
  const boxInfo = {
    sizes: [DROPDOWN.BOX_OPTION[0]], // 12_4, 4_6
    package_types: ['single'], // single, mix
    package_ids: [...selectedPackage],
  };

  const handleBoxInfo = () => {
    setBoxInfo(boxInfo);
    setOpenPopUp(false);
  };

  return (
    <div className="p-3 ">
      <div className="d-flex flex-column gap-3  ms-4">
        <div className="d-flex justify-content-between ms-5 me-5">
          <label>{TEXT.BOX_SIZE}</label>
          <DropDown
            dropdownMenu={DROPDOWN.BOX_OPTION}
            value={option}
            setValue={setOption}
            printBox={true}
          />
        </div>
        <div className="d-flex justify-content-between ms-5 me-5">
          <label>{TEXT.BOX_TYPE}</label>
          <Input
            className="w-50 ps-2"
            type="text"
            disabled={true}
            value={boxInfo.package_types[0]}
          />
        </div>
        <div className="d-flex justify-content-center gap-3">
          <Button
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3"
            clickHandler={handleBoxInfo}
            text={BUTTON.SAVE}></Button>
          <Button
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3"
            clickHandler={() => setOpenPopUp(false)}
            text={BUTTON.CANCEL}></Button>
        </div>
      </div>
    </div>
  );
};

/**
 * isUnitBoxRow - whether a BLP row is an eligible-for-conversion unit box: the batch's
 * component SKU at exactly one full box's worth of units (units_per_pack * packs_per_box).
 * Not gated on three_pack.converted — converting some boxes must not block the rest;
 * a converted box's row naturally stops matching (its sku/qty become the 3-pack ones).
 */
const isUnitBoxRow = (row, selectedBatch) => {
  const threePack = selectedBatch?.three_pack;
  if (!isThreePackEligible(threePack, selectedBatch?.pack_qty)) return false;
  return row.sku === selectedBatch.SKU && row.qty === threePack.units_per_pack * threePack.packs_per_box;
};

/**
 * BLPList - Box Label Package list component
 * @param {Array} data - Array of BLP data
 * @param {Object} selectedBatch - The batch this BLP list belongs to (for 3-pack eligibility)
 * @param {Function} setEditBoxState - Function to control edit box state
 * @param {Function} setReload - Function to trigger data reload
 * @param {Function} setBlpData - Function to replace the BLP list (used by 3-pack conversion)
 */
export const BLPList = ({ data, selectedBatch, setEditBoxState, setReload, setBlpData }) => {
  const { setLoading } = useLoader();
  const [openPopUp, setOpenPopUp] = useState(false);
  const [boxInfo, setBoxInfo] = useState({});
  const [printUrl, setPrintUrl] = useState('');
  const [confirmRow, setConfirmRow] = useState(null); // row pending Convert-to-3-Pack confirmation
  const [convertingId, setConvertingId] = useState(null); // any in-flight conversion disables every row's button
  const sortedData = [...data].sort((first, second) => String(first.name).localeCompare(String(second.name)));

  const handleConvertRow = async () => {
    const row = confirmRow;
    setConfirmRow(null);
    setConvertingId(row.id);
    try {
      const response = await generateThreePackBlp({
        data: { batch_id: selectedBatch.id, package_ids: [row.id] }
      });
      if (!response) return; // fetchData already showed the error toast

      const refreshed = await getPackageList({ data: { batch_id: [selectedBatch.id] } });
      if (refreshed?.data) setBlpData(refreshed.data);

      popup(POPUP_TYPE.SUCCESS, response.message);
    } finally {
      setConvertingId(null);
    }
  };

  const columns = [
    ...BLP_COLUMNS,
    {
      name: 'Action',
      classNameTh: '',
      classNameTd: '',
      isCustom: true,
      dataField: ['id', 'name', 'sku', 'qty'],
      customFormatter: (row) =>
        isUnitBoxRow(row, selectedBatch) ? (
          <Button
            clickHandler={() => setConfirmRow(row)}
            disabled={convertingId !== null}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-1 px-2">
            {BUTTON.CONVERT_TO_THREE_PACK}
          </Button>
        ) : null,
    },
  ];

  const getPrintUrl = useDebounce(async () => {
    popup(POPUP_TYPE.SUCCESS, 'Label Printing Initiated');
    try {
      const response = await getPrint({
        data: {
          sizes: [DROPDOWN.BOX_OPTION[0]], // 12_4, 4_6
          package_types: ['single'], // single, mix
          package_ids: [...data.map((pck) => pck.id)],
        },
      });
      if (!response) return; // fetchData already showed the error toast

      if (response.data?.length > 0) {
        setPrintUrl(response.data[0].split('?')[0]);
      } else {
        popup(POPUP_TYPE.ERR, response.message);
      }
    } finally {
      setLoading(false);
    }
  }, 1000);

  useEffect(() => {
    if (Object.keys(boxInfo).length) {
      setLoading(true);
      getPrintUrl();
    }
  }, [boxInfo, getPrintUrl, setLoading]);

  useEffect(() => {
    if (printUrl) {
      openProfileDocument(printUrl, '_blank', 'noreferrer');
      setEditBoxState(EDIT_BOX_STATE.CLOSED);
      setReload?.(true);
    }
  }, [printUrl, setEditBoxState, setReload]);

  return data.length > 0 ? (
    <div className="edit-container p-4">
      <h5 className="text-center mb-4 mt-0">{HEADING.LABELS}</h5>
      <div className="w-100 table-3">
        <MfgTable column={columns} data={sortedData} clickHandler={() => { }} />
      </div>
      {openPopUp && (
        <PopUpBox openPopUpBox={openPopUp}>
          <BoxInfo
            selectedPackage={data.map((pck) => pck.id)}
            setOpenPopUp={setOpenPopUp}
            setBoxInfo={setBoxInfo}
          />
        </PopUpBox>
      )}
      {confirmRow && (
        <PopUpBox openPopUpBox={!!confirmRow}>
          <div className="p-3">
            <p className="text-center text-special placeholder-color">
              {POPUP_MSG.CONFIRM_THREE_PACK_CONVERSION(
                confirmRow.name,
                confirmRow.qty,
                confirmRow.sku,
                selectedBatch.three_pack.packs_per_box,
                selectedBatch.three_pack.pack_sku
              )}
            </p>
            <hr />
            <div className="d-flex align-items-center justify-content-center gap-4 w-100 mt-2">
              <Button
                clickHandler={handleConvertRow}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {BUTTON.CONFIRM}
              </Button>
              <Button
                clickHandler={() => setConfirmRow(null)}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {BUTTON.CANCEL}
              </Button>
            </div>
          </div>
        </PopUpBox>
      )}
      <div className="d-flex align-items-center justify-content-center gap-4 w-100 mt-4">
        <Button
          clickHandler={getPrintUrl}
          className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
          {BUTTON.PRINT_LABEL}
        </Button>
        <Button
          clickHandler={() => {
            setEditBoxState(EDIT_BOX_STATE.CLOSED);
            setReload?.(true);
          }}
          className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
          {BUTTON.CLOSE}
        </Button>
      </div>
    </div>
  ) : null;
};
